import { toWSDateString } from "@wingspanhq/fe-component-library";
import { Payroll, PayrollCycleType } from "@wingspanhq/wingspan-api-sdk";

import { PayrollRunSummary } from "../types";
import {
  formatPayPeriodLabel,
  formatPayPeriodRange,
  parsePayrollAmount
} from "../utils";

import { selectorPayrollRunStatus } from "./selectorPayrollRunStatus";

/** A short secondary status line for the history table, derived from status + events. */
const selectStatusDetail = (payroll: Payroll): string | undefined => {
  switch (selectorPayrollRunStatus(payroll.status)) {
    case "pending":
      return "Payroll pending";
    case "opened":
      return "Open";
    case "processing":
      return "Processing";
    case "failed":
      return "Payroll failed";
    case "completed":
      return payroll.events?.paidAt
        ? `Paid ${toWSDateString(payroll.events.paidAt, "monthDayYear")}`
        : "Paid";
    default:
      return undefined;
  }
};

/**
 * Adapt a v3 `Payroll` into the list/card view model.
 *
 * TODO(v3): the v3 `Payroll` carries no run-level rollups, so `grossAmount`,
 * `payerTaxes`, `benefits` and `paystubsCount` are 0 here — populating them per
 * row would need an N+1 pay-statement fetch. They ARE computed on the details
 * page (which already loads the run's pay statements). `liabilityAmount` uses
 * the one run-level total the API exposes (`totalAmount`).
 */
export const selectorPayrollRunSummary = (
  payroll: Payroll
): PayrollRunSummary => ({
  id: payroll.id ?? "",
  payPeriodLabel: formatPayPeriodLabel(payroll.payPeriodStartDate),
  payPeriodRange: formatPayPeriodRange(
    payroll.payPeriodStartDate,
    payroll.payPeriodEndDate
  ),
  isOffCycle: payroll.cycleType === PayrollCycleType.OFF_CYCLE,
  liabilityAmount: parsePayrollAmount(payroll.totalAmount),
  grossAmount: 0,
  payerTaxes: 0,
  benefits: 0,
  payrollId: payroll.payrollNumber ?? payroll.id ?? "",
  finalizationDate: payroll.events?.finalizedAt ?? undefined,
  status: selectorPayrollRunStatus(payroll.status),
  statusDetail: selectStatusDetail(payroll),
  paystubsCount: 0
});
